import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box, Typography, Paper, TextField, Button, Alert,
  Divider, Checkbox, FormControlLabel, CircularProgress, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReactMarkdown from 'react-markdown';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../auth/AuthContext';
import {
  getLegalStatus, signBaa, getBaaRecord,
  signGlobalProcessingWaiver, disableGlobalProcessing,
} from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import {
  BAA_CONTENT, GLOBAL_WAIVER_LIVE_CONTENT, GLOBAL_WAIVER_LIVE_NOBAA_CONTENT,
  GLOBAL_WAIVER_TEST_CONTENT,
} from '../../assets/legalContent';

export default function SettingsPage() {
  const { user, changePassword } = useAuth();
  const queryClient = useQueryClient();
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState('');
  const [baaAgreed, setBaaAgreed] = useState(false);
  const [baaError, setBaaError] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();

  const { data: legalStatus, error: legalError } = useQuery('legal-status', getLegalStatus, { staleTime: 60 * 1000 });
  const { data: baaRecord, error: baaRecordError } = useQuery('baa-record', getBaaRecord, {
    enabled: !!legalStatus?.baaAccepted,
    staleTime: Infinity,
  });

  const { mutate: doSignBaa, isLoading: signingBaa } = useMutation(signBaa, {
    onSuccess: () => {
      queryClient.invalidateQueries('legal-status');
      queryClient.invalidateQueries('baa-record');
      setBaaAgreed(false);
    },
    onError: (e) => setBaaError(e?.response?.data?.message || 'Signing failed. Please try again.'),
  });

  const onPasswordSubmit = async ({ oldPassword, newPassword }) => {
    setPwError(''); setPwSuccess(false);
    try {
      await changePassword(oldPassword, newPassword);
      setPwSuccess(true);
      reset();
    } catch (e) {
      setPwError(e.message || 'Password change failed.');
    }
  };

  const baaAvailable = legalStatus?.baaAvailable;
  const baaAccepted = legalStatus?.baaAccepted;
  const baaSignedAt = legalStatus?.baaSignedAt
    ? new Date(legalStatus.baaSignedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  // #264 H4 — the production (live) waiver wording keys on whether the account HOLDS a BAA,
  // not on live-vs-test. A BAA-holder signs the strong PHI waiver (and must hold a CURRENT BAA —
  // a stale one is rejected server-side with 409); a no-BAA account signs the production, non-PHI
  // residency/pricing waiver and is never forced into a BAA. (The test tenant is unaffected.)
  const hasBaa = !!legalStatus?.baaSignedAt;
  const liveWaiverDoc = hasBaa ? GLOBAL_WAIVER_LIVE_CONTENT : GLOBAL_WAIVER_LIVE_NOBAA_CONTENT;
  const liveReacceptRequired = hasBaa && !baaAccepted;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Account preferences</Typography>

      <ApiErrorAlert error={legalError || baaRecordError} sx={{ mb: 2 }} />

      {/* Account info */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 480, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>Account</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Name</Typography>
            <Typography variant="body2">{user?.firstName} {user?.lastName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography variant="body2">{user?.email}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Account ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{user?.partnerId || '—'}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Change password */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 480, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>Change password</Typography>
        <Divider sx={{ mb: 2 }} />
        {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>Password updated.</Alert>}
        {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
        <Box component="form" onSubmit={handleSubmit(onPasswordSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Current password" type="password" size="small" fullWidth
            {...register('oldPassword', { required: true })} />
          <TextField label="New password" type="password" size="small" fullWidth
            {...register('newPassword', { required: true, minLength: 8 })}
            helperText="Minimum 8 characters" />
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={{
            alignSelf: 'flex-start', bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' },
            textTransform: 'none', fontWeight: 600,
          }}>
            {isSubmitting ? 'Updating…' : 'Update password'}
          </Button>
        </Box>
      </Paper>

      {/* HIPAA BAA */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 720 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>HIPAA Business Associate Agreement</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Required before transmitting Protected Health Information (PHI) via the Vectros API.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {!baaAvailable && (
          <Alert severity="info" sx={{ mb: 0 }}>
            HIPAA BAA is available on the Pro plan and above.{' '}
            <Link to="/billing" style={{ color: 'inherit', fontWeight: 600 }}>Upgrade to Pro →</Link>
          </Alert>
        )}

        {baaAvailable && baaAccepted && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>Signed</Typography>
              <Chip label={`Signed ${baaSignedAt}`} size="small" sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 500 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Signed by: {baaRecord?.signerName} ({baaRecord?.signerEmail})
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setViewOpen(true)}
              sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
              View signed agreement
            </Button>
          </Box>
        )}

        {baaAvailable && !baaAccepted && (
          <Box>
            <Box sx={{
              height: 320, overflowY: 'auto', border: '1px solid #E4E4E7',
              borderRadius: 1, bgcolor: '#FAFAFA', p: 2, mb: 2,
              '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.7, fontSize: 13 },
              '& ul,ol': { pl: 3, mb: 1.5 },
              '& li': { fontSize: 13, mb: 0.5 },
            }}>
              <ReactMarkdown>{BAA_CONTENT}</ReactMarkdown>
            </Box>

            {baaError && <Alert severity="error" sx={{ mb: 2 }}>{baaError}</Alert>}

            <FormControlLabel
              control={<Checkbox checked={baaAgreed} onChange={(e) => setBaaAgreed(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  I am authorized to execute this agreement on behalf of <strong>{user?.firstName} {user?.lastName}</strong> and agree to its terms
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              disabled={!baaAgreed || signingBaa}
              onClick={() => doSignBaa()}
              sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, fontWeight: 600, textTransform: 'none' }}
            >
              {signingBaa ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Sign Business Associate Agreement'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Data Residency & Region (ADR-0046 / #260) */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, maxWidth: 720, mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>Data Residency &amp; Region</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          By default, all inference runs in pinned US regions. You may opt a tenant into global (non-US)
          processing for lower cost by signing the global-processing waiver below. If you hold a Business
          Associate Agreement, the production (live) waiver carries stronger PHI wording and requires a
          current BAA; without a BAA it is a residency-and-pricing election only (no PHI).
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ResidencyTenantSection
          tenant="live" label="Production (live) tenant"
          status={legalStatus?.residency?.live}
          waiverDoc={liveWaiverDoc}
          reacceptRequired={liveReacceptRequired}
          baaAvailable={baaAvailable}
          queryClient={queryClient} user={user}
        />
        <Divider sx={{ my: 2 }} />
        <ResidencyTenantSection
          tenant="test" label="Test tenant"
          status={legalStatus?.residency?.test}
          waiverDoc={GLOBAL_WAIVER_TEST_CONTENT}
          queryClient={queryClient} user={user}
        />
      </Paper>

      {/* View signed BAA modal */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Signed Business Associate Agreement</DialogTitle>
        <DialogContent dividers sx={{
          '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
          '& p': { mb: 1.5, lineHeight: 1.7, fontSize: 13 },
          '& ul,ol': { pl: 3, mb: 1.5 },
          '& li': { fontSize: 13, mb: 0.5 },
        }}>
          {baaRecord?.signedContent
            ? <ReactMarkdown>{baaRecord.signedContent}</ReactMarkdown>
            : <CircularProgress size={24} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Per-tenant Data Residency control (ADR-0046 / #260). Shows the current served-region posture and,
 * if no current waiver, the (DRAFT) global-processing waiver + an attestation + the two posture
 * choices (default-global, allow-per-request-override). Signing sets the posture in one act; a
 * Disable button resets the tenant to US-pinned (no waiver needed).
 */
function ResidencyTenantSection({ tenant, label, status, waiverDoc, reacceptRequired, baaAvailable, queryClient, user }) {
  const [agreed, setAgreed] = useState(false);
  const [defaultGlobal, setDefaultGlobal] = useState(false);
  const [allowOverride, setAllowOverride] = useState(true);
  const [error, setError] = useState('');
  const [showDoc, setShowDoc] = useState(false);

  const accepted = !!status?.globalWaiverAccepted;
  const servedDefault = status?.residencyZone === 'global' ? 'Global' : 'US (pinned)';
  const overrideAllowed = !!status?.allowGlobalOverride;
  const signedAt = status?.globalWaiverSignedAt
    ? new Date(status.globalWaiverSignedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const refresh = () => queryClient.invalidateQueries('legal-status');

  const { mutate: doSign, isLoading: signing } = useMutation(
    () => signGlobalProcessingWaiver(tenant, { defaultGlobal, allowOverride }),
    {
      onSuccess: () => { refresh(); setAgreed(false); setError(''); },
      onError: (e) => setError(e?.response?.data?.message || 'Signing failed. Please try again.'),
    },
  );

  const { mutate: doDisable, isLoading: disabling } = useMutation(
    () => disableGlobalProcessing(tenant),
    {
      onSuccess: () => { refresh(); setError(''); },
      onError: (e) => setError(e?.response?.data?.message || 'Failed to disable. Please try again.'),
    },
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Chip
          size="small"
          label={`Default: ${servedDefault}`}
          sx={{ bgcolor: servedDefault.startsWith('US') ? '#f0fdf4' : '#fef3c7',
                color: servedDefault.startsWith('US') ? '#166534' : '#92400e', fontWeight: 500 }}
        />
        {overrideAllowed && (
          <Chip size="small" label="Per-request global allowed"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }} />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {accepted ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              Global-processing waiver signed{signedAt ? ` ${signedAt}` : ''}.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" disabled={disabling}
            onClick={() => doDisable()}
            sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
            {disabling ? <CircularProgress size={16} /> : 'Disable global processing (return to US-pinned)'}
          </Button>
        </Box>
      ) : reacceptRequired ? (
        // #264 H4 — a BAA-holder whose BAA is stale cannot sign the production waiver until they
        // re-accept the current BAA (the server returns 409). Block here and direct them to the
        // actionable path: re-accept above, or (if their plan no longer offers the BAA) upgrade
        // to Pro first — otherwise the direction would point at a section that only shows "Upgrade".
        <Alert severity="warning" sx={{ mb: 0 }}>
          {baaAvailable ? (
            <>
              Your Business Associate Agreement has been updated. Please re-accept your current BAA in the{' '}
              <strong>HIPAA Business Associate Agreement</strong> section above before signing a
              global-processing waiver for this production tenant.
            </>
          ) : (
            <>
              Your Business Associate Agreement is out of date, and re-accepting it requires a Pro plan.{' '}
              <Link to="/billing" style={{ color: 'inherit', fontWeight: 600 }}>Upgrade to Pro</Link> and
              re-accept your BAA to sign a production global-processing waiver — or sign the waiver for your
              test tenant instead.
            </>
          )}
        </Alert>
      ) : (
        <Box>
          <Button size="small" onClick={() => setShowDoc((v) => !v)}
            sx={{ textTransform: 'none', mb: 1, pl: 0 }}>
            {showDoc ? 'Hide waiver' : 'Review global-processing waiver'}
          </Button>
          {showDoc && (
            <Box sx={{
              height: 220, overflowY: 'auto', border: '1px solid #E4E4E7', borderRadius: 1,
              bgcolor: '#FAFAFA', p: 2, mb: 2,
              '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.7, fontSize: 13 },
              '& ul,ol': { pl: 3, mb: 1.5 }, '& li': { fontSize: 13, mb: 0.5 },
              '& blockquote': { borderLeft: '3px solid #f59e0b', pl: 1.5, color: '#92400e' },
            }}>
              <ReactMarkdown>{waiverDoc}</ReactMarkdown>
            </Box>
          )}
          <FormControlLabel
            control={<Checkbox size="small" checked={defaultGlobal}
              onChange={(e) => setDefaultGlobal(e.target.checked)} />}
            label={<Typography variant="body2">Default this tenant to global (every request global unless it opts back to US)</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={allowOverride}
              onChange={(e) => setAllowOverride(e.target.checked)} />}
            label={<Typography variant="body2">Allow individual requests to opt into global (per-request <code>allowGlobalRegion</code>)</Typography>}
          />
          <FormControlLabel
            sx={{ mt: 1, mb: 2, display: 'block' }}
            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
            label={
              <Typography variant="body2">
                I am authorized to make this election on behalf of{' '}
                <strong>{user?.firstName} {user?.lastName}</strong> and accept the waiver terms above
              </Typography>
            }
          />
          <Button
            variant="contained"
            disabled={!agreed || signing || (!defaultGlobal && !allowOverride)}
            onClick={() => doSign()}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, fontWeight: 600, textTransform: 'none' }}
          >
            {signing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Sign global-processing waiver'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
